/**
 * Профіль користувача (рішення власника 2026-08-31): дані юзера +
 * СХОВИЩЕ ВКЛАДЕНЬ по-організаційно — квота за тарифом бази (ч.1398), % зайнятості
 * (progress-bar) і розбивка по модулях з кнопкою «очистити» per-модуль
 * (deletion_mark + файли з диска; незворотньо — через confirm).
 */
import { useMemo, useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { attachments, type StorageUsageOrg } from '@/api/attachments';
import { orgs } from '@/api/catalogs';
import { useConfirm } from '@/components/ConfirmProvider';
import { usePermissions } from '@/auth/usePermissions';
import DataTable, { type ColumnDef } from '@/shared/components/DataTable';
import { MODULE_NAMES_UK } from '@/utils/moduleNames';

/** Модулі вкладень поза MODULE_NAMES_UK (штампи/інше). */
const EXTRA_MODULE_NAMES: Record<string, string> = {
  stamps: 'Печатки і підписи',
  dps_cabinet: 'Кабінет ДПС',
  other: 'Інше',
};

const modLabel = (m: string) => MODULE_NAMES_UK[m] ?? EXTRA_MODULE_NAMES[m] ?? m;

const fmtBytes = (n: number): string => {
  if (n >= 1024 * 1024 * 1024) return `${(n / (1024 ** 3)).toFixed(2)} ГБ`;
  if (n >= 1024 * 1024) return `${(n / (1024 ** 2)).toFixed(1)} МБ`;
  if (n >= 1024) return `${(n / 1024).toFixed(0)} КБ`;
  return `${n} Б`;
};

function OrgStorageCard({ u, orgName, canManage }: {
  u: StorageUsageOrg; orgName: string; canManage: boolean;
}) {
  const qc = useQueryClient();
  const confirm = useConfirm();
  // ч.1408: квота null — обсяг не обмежується (картка ЕДО без оплачених блоків).
  const pct = u.quota_bytes ? Math.min(100, Math.round((u.used_bytes / u.quota_bytes) * 100)) : 0;
  const barColor = pct >= 90 ? '#dc2626' : pct >= 70 ? '#f59e0b' : '#2563eb';
  const cleanup = useMutation({
    mutationFn: (module: string) => attachments.cleanupModule(u.organization_id, module),
    onSuccess: () => qc.invalidateQueries({ queryKey: ['attachments-storage-usage'] }),
  });
  type Row = StorageUsageOrg['modules'][number];
  const cols = useMemo<ColumnDef<Row>[]>(() => [
    {
      key: 'module', header: 'Модуль',
      sortValue: (r) => modLabel(r.module),
      cell: (r) => modLabel(r.module),
    },
    {
      key: 'files', header: 'Файлів', width: 110, align: 'right',
      sortValue: (r) => r.files, cell: (r) => r.files,
    },
    {
      key: 'bytes', header: 'Обсяг', width: 130, align: 'right',
      sortValue: (r) => r.bytes, cell: (r) => fmtBytes(r.bytes),
    },
    {
      key: 'share', header: '% сховища', width: 110, align: 'right',
      sortValue: (r) => r.bytes,
      cell: (r) => u.used_bytes ? `${Math.round((r.bytes / u.used_bytes) * 100)}%` : '0%',
    },
    ...(canManage ? [{
      key: 'actions', header: '', width: 110, align: 'center',
      cell: (r) => (
        <button className="danger" style={{ padding: '0.1rem 0.6rem', fontSize: '0.78rem' }}
          disabled={cleanup.isPending}
          onClick={() => {
            void confirm({
              title: 'Очистити модуль',
              message: `Видалити ВСІ вкладення модуля «${modLabel(r.module)}» `
                + `у «${orgName}» (${r.files} файл(ів), ${fmtBytes(r.bytes)})? `
                + 'Дію не можна скасувати.',
              okLabel: 'видалити', cancelLabel: 'скасувати', danger: true,
            }).then((ok) => { if (ok) cleanup.mutate(r.module); });
          }}>
          очистити
        </button>
      ),
    } as ColumnDef<Row>] : []),
  ], [u.used_bytes, canManage, cleanup.isPending, orgName]);

  // ч.961 (зауваження власника): дані про сховище — МЕНШИМИ картками,
  // а таблиця по модулях — попапом (клік «детальніше»).
  const [open, setOpen] = useState(false);
  return (
    <>
      <div style={{ border: '1px solid var(--border-soft)', borderRadius: 8,
                    padding: '0.6rem 0.7rem', minWidth: 230, flex: '1 1 260px' }}>
        <div style={{ fontWeight: 600, fontSize: '0.88rem', whiteSpace: 'nowrap',
          overflow: 'hidden', textOverflow: 'ellipsis' }} title={orgName}>{orgName}</div>
        <div style={{ height: 6, background: 'var(--border)', borderRadius: 3, margin: '6px 0 5px' }}>
          <div style={{ width: `${Math.max(pct, u.used_bytes > 0 ? 2 : 0)}%`, height: '100%',
                        background: barColor, borderRadius: 3 }} />
        </div>
        <div style={{ display: 'flex', alignItems: 'baseline', gap: 6, flexWrap: 'wrap' }}>
          <span style={{ fontSize: '0.85rem', fontWeight: 600 }}>{fmtBytes(u.used_bytes)}</span>
          <span style={{ color: 'var(--muted)', fontSize: '0.78rem' }}>
            {u.quota_bytes == null ? '· обсяг не обмежується' : `з ${fmtBytes(u.quota_bytes)} · ${pct}%`}
          </span>
          <button type="button" style={{ marginLeft: 'auto', padding: 0, border: 'none',
            background: 'none', color: 'var(--primary)', cursor: 'pointer', fontSize: '0.78rem' }}
            onClick={() => setOpen(true)}>детальніше</button>
        </div>
      </div>
      {open && (
        <div style={{ position: 'fixed', inset: 0, zIndex: 1100, background: 'var(--overlay)',
          display: 'flex', alignItems: 'center', justifyContent: 'center' }}
          onClick={() => setOpen(false)}>
          <div className="card" style={{ width: 720, maxWidth: '95vw', maxHeight: '80vh',
            overflowY: 'auto', margin: 0 }} onClick={(e) => e.stopPropagation()}>
            <div className="toolbar">
              <h3 style={{ margin: 0 }}>Сховище: {orgName}</h3>
              <button style={{ marginLeft: 'auto' }} onClick={() => setOpen(false)}>закрити</button>
            </div>
            <div style={{ color: 'var(--muted)', fontSize: '0.85rem', marginBottom: '0.5rem' }}>
              Зайнято {fmtBytes(u.used_bytes)}{u.quota_bytes == null ? ' — обсяг не обмежується' : ` з ${fmtBytes(u.quota_bytes)} (${pct}%)`}.
            </div>
            {u.modules.length === 0
              ? <div style={{ color: 'var(--muted)', fontSize: '0.85rem' }}>Вкладень немає.</div>
              : <DataTable<Row> rows={u.modules} columns={cols} rowKey={(r) => r.module}
                  emptyText="Вкладень немає." initialSortKey="bytes" initialSortDir="desc" />}
          </div>
        </div>
      )}
    </>
  );
}

export default function ProfilePage() {
  const orgList = useQuery({ queryKey: ['orgs'], queryFn: () => orgs.list({ limit: 200 }) });
  const usage = useQuery({
    queryKey: ['attachments-storage-usage'],
    queryFn: () => attachments.storageUsage(),
  });
  // ч.1104 (рішення власника): очищати сховище може лише власник або адміністратор
  // акаунта; право «перегляд сховища» дає тільки перегляд.
  const { isOwnerOrAdmin } = usePermissions();
  const orgName = (id: string) =>
    orgList.data?.items.find((o) => o.id === id)?.name ?? id;

  // Стабільний порядок — за назвою організації.
  const usageSorted = [...(usage.data ?? [])].sort(
    (a, b) => orgName(a.organization_id).localeCompare(orgName(b.organization_id), 'uk'));

  return (
    <div className="card">
      {/* ч.961 (зауваження власника): блок з ПІБ/email/роллю прибрано —
          ці дані є у розділі «Облікові дані». */}
      <div className="toolbar">
        <h2 style={{ margin: 0, fontSize: '1.05rem' }}>Сховище вкладень</h2>
      </div>
      <div style={{ color: 'var(--muted)', fontSize: '0.85rem', marginBottom: '0.5rem' }}>
        Квота — за тарифом кожної організації («Баланс і тарифи»); картка ЕДО без оплаченого сховища обсягом не обмежується.
        Очистка видаляє всі вкладення модуля організації безповоротно.
      </div>
      {usage.isLoading && <div style={{ color: 'var(--muted)' }}>Завантаження…</div>}
      {usage.isError && (
        <div style={{ color: 'var(--muted)' }}>
          Немає доступу до сховища вкладень (потрібне право на вкладення документів).
        </div>
      )}
      {usage.data && usageSorted.length === 0 && (
        <div style={{ color: 'var(--muted)' }}>Вкладень поки немає у жодній організації.</div>
      )}
      <div style={{ display: 'flex', flexWrap: 'wrap', gap: '0.6rem' }}>
        {usageSorted.map((u) => (
          <OrgStorageCard key={u.organization_id} u={u}
            orgName={orgName(u.organization_id)}
            canManage={isOwnerOrAdmin} />
        ))}
      </div>
    </div>
  );
}
